import LoadingAnimation from 'components/LoadingAnimation'
import PMBTooltip from 'components/PMBTooltip'
import usePartialUpload from 'hooks/usePartialUpload'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Eye, Upload, X } from 'react-feather'
import { Button, Form, FormGroup, Input, Label, Progress } from 'reactstrap'

const PMBFileUploads = ({
  label = 'Upload File(s)',
  multiple = false,
  maxFiles = 5,
  maxFileSize = 1 * 1024 * 1024, // DEFAULT 1MB/FILE
  accept = '.png, .jpeg, .jpg, .pdf',
  value = [],
  onChange,
  disabled = false,
  required = false,
  uploadParams = {
    userId: 'FILE',
    source: 'PROJECT',
    isPublic: true,
  },
  testId = 'pmb-file-uploads',
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { uploadFile } = usePartialUpload()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState([])

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files)
    if (!selectedFiles.length) return

    const currentFileCount = value.length
    const newFileCount = selectedFiles.length

    if (multiple && currentFileCount + newFileCount > maxFiles) {
      enqueueSnackbar(`Maksimal ${maxFiles} file`, {
        variant: 'error',
      })
      e.target.value = null
      return
    }

    if (!multiple && newFileCount > 1) {
      enqueueSnackbar('Hanya dapat mengunggah 1 file', {
        variant: 'error',
      })
      e.target.value = null
      return
    }

    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > maxFileSize,
    )
    if (oversizedFiles.length > 0) {
      enqueueSnackbar(
        `Ukuran file maksimal ${formatFileSize(maxFileSize)}. File "${oversizedFiles[0].name}" terlalu besar (${formatFileSize(oversizedFiles[0].size)})`,
        {
          variant: 'error',
        },
      )
      e.target.value = null
      return
    }

    setIsUploading(true)

    const initialUploadingFiles = selectedFiles.map((file, i) => ({
      id: Date.now() + i,
      fileName: file.name,
      progress: 0,
      status: 'uploading',
    }))
    setUploadingFiles(initialUploadingFiles)

    let newUploadedFiles = []

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const fileId = initialUploadingFiles[i].id

        const uploadedFile = await uploadFile(
          selectedFiles[i],
          (percentage) => {
            setUploadingFiles((prev) =>
              prev.map((f) =>
                f.id === fileId ? { ...f, progress: percentage } : f,
              ),
            )
          },
          uploadParams.userId,
          uploadParams.source,
          uploadParams.isPublic,
        )

        if (!uploadedFile?.url) {
          enqueueSnackbar(uploadedFile?.payload || 'Upload gagal', {
            variant: 'error',
          })
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileId ? { ...f, status: 'error', progress: 0 } : f,
            ),
          )
        } else {
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === fileId ? { ...f, status: 'success', progress: 100 } : f,
            ),
          )
          newUploadedFiles.push({
            id: fileId,
            fileUrl: uploadedFile?.url,
            fileName: selectedFiles[i].name,
            tag: '',
          })
        }
      }

      if (multiple) {
        onChange([...value, ...newUploadedFiles])
      } else {
        onChange(newUploadedFiles)
      }

      setTimeout(() => {
        setUploadingFiles([])
      }, 1000)
    } catch (err) {
      enqueueSnackbar('Terjadi kesalahan saat upload file', {
        variant: 'error',
      })
      setUploadingFiles([])
    } finally {
      setIsUploading(false)
      e.target.value = null
    }
  }

  const handleRemoveFile = (fileId) => {
    const filteredFiles = value.filter((file) => file.id !== fileId)
    onChange(filteredFiles)
  }

  const handleViewFile = (fileUrl) => {
    window.open(fileUrl, '_blank')
  }

  const handleTagChange = (fileId, newTag) => {
    const updatedFiles = value.map((file) =>
      file.id === fileId ? { ...file, tag: newTag } : file,
    )
    onChange(updatedFiles)
  }

  const canUploadMore = multiple ? value.length < maxFiles : value.length === 0

  return (
    <FormGroup>
      {label && (
        <Label data-testid={`${testId}-label`}>
          {label}
          {required && <span style={{ color: '#b42218' }}> *</span>}
          {multiple && (
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {' '}
              (Maksimal {maxFiles} file, {formatFileSize(maxFileSize)} per file)
            </span>
          )}
          {!multiple && (
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              {' '}
              (Maksimal {formatFileSize(maxFileSize)})
            </span>
          )}
        </Label>
      )}

      <Form
        encType="multipart/form-data"
        className="border rounded"
        style={{ overflow: 'hidden' }}
      >
        <div className="d-flex align-items-center">
          <fieldset className="fieldset" style={{ marginBottom: 0 }}>
            <input
              className="input-file"
              id="pmbFileUpload"
              disabled={disabled || isUploading || !canUploadMore}
              name="pmbFileUpload"
              onChange={handleFileChange}
              type="file"
              accept={accept}
              multiple={multiple}
              data-testid={`${testId}-input`}
            />
            <label
              htmlFor="pmbFileUpload"
              className="d-flex align-items-center gap-2 mb-0"
              data-testid={`${testId}-button`}
              style={{
                cursor:
                  disabled || isUploading || !canUploadMore
                    ? 'not-allowed'
                    : 'pointer',
                opacity: disabled || isUploading || !canUploadMore ? 0.6 : 1,
              }}
            >
              {isUploading ? (
                <LoadingAnimation color="primary" size="sm" />
              ) : (
                <>
                  <Upload size={16} />
                  Upload
                </>
              )}
            </label>
          </fieldset>

          <div className="flex-grow-1" style={{ padding: '10px 14px' }}>
            {value.length > 0 && !isUploading ? (
              <span className="text-muted">
                {value.length} file{value.length > 1 ? 's' : ''} terupload
              </span>
            ) : (
              <span className="text-muted">Belum ada file</span>
            )}
          </div>
        </div>
      </Form>

      {uploadingFiles.length > 0 && (
        <div className="mt-3">
          <div className="mb-2 fw-semibold text-sm">Sedang mengupload...</div>
          {uploadingFiles.map((file, index) => (
            <div
              key={file.id}
              className="border rounded p-3 mb-2"
              data-testid={`${testId}-progress-item`}
              style={{
                backgroundColor:
                  file.status === 'success'
                    ? '#f0fdf4'
                    : file.status === 'error'
                      ? '#fef2f2'
                      : '#f9fafb',
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="flex-shrink-0 fw-bold text-muted">
                  {index + 1}.
                </div>
                <div className="flex-grow-1">
                  <div
                    className="text-sm mb-1"
                    style={{
                      fontWeight: 500,
                      color: file.status === 'error' ? '#dc2626' : '#344054',
                    }}
                  >
                    {file.fileName}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Progress
                      value={file.progress}
                      className="flex-grow-1"
                      style={{ height: '8px' }}
                      color={
                        file.status === 'success'
                          ? 'success'
                          : file.status === 'error'
                            ? 'danger'
                            : 'primary'
                      }
                    />
                    <span
                      className="text-sm fw-semibold"
                      style={{
                        minWidth: '45px',
                        color:
                          file.status === 'success'
                            ? '#16a34a'
                            : file.status === 'error'
                              ? '#dc2626'
                              : '#0ea5e9',
                      }}
                    >
                      {file.progress}%
                    </span>
                  </div>
                </div>
                {/* {file.status === 'success' && (
                  <CheckCircle size={20} color="#16a34a" />
                )} */}
              </div>
            </div>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-3">
          {value.map((file, index) => (
            <div
              key={file.id}
              className="border rounded p-3 mb-2"
              data-testid={`${testId}-item-${file.id}`}
              style={{ backgroundColor: '#f9fafb' }}
            >
              <div className="d-flex gap-2">
                <div className="flex-shrink-0 fw-bold text-muted">
                  {index + 1}.
                </div>
                <div className="flex-grow-1">
                  <div className="mb-2">
                    <Label className="text-sm mb-1">Keterangan</Label>
                    <Input
                      type="text"
                      placeholder="Input keterangan..."
                      value={file.tag || ''}
                      onChange={(e) => handleTagChange(file.id, e.target.value)}
                      disabled={disabled}
                      size="sm"
                      data-testid={`${testId}-item-tag-input-${file.id}`}
                    />
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {file.fileName}
                  </div>
                </div>
                <div
                  className="d-flex gap-2 flex-shrink-0 align-self-start"
                  style={{ marginTop: '24px' }}
                >
                  <Button
                    id={`view-file-${file.id}`}
                    color="primary"
                    size="sm"
                    onClick={() => handleViewFile(file.fileUrl)}
                    disabled={disabled}
                    data-testid={`${testId}-item-view-${file.id}`}
                    // style={{ minWidth: '70px' }}
                    style={{ width: '100%' }}
                  >
                    <Eye size={14} />
                  </Button>
                  <PMBTooltip target={`view-file-${file.id}`} text="Lihat" />
                  <Button
                    id={`remove-file-${file.id}`}
                    color="danger"
                    size="sm"
                    onClick={() => handleRemoveFile(file.id)}
                    disabled={disabled}
                    data-testid={`${testId}-item-remove-${file.id}`}
                    style={{
                      // minWidth: '50px',
                      width: '100%',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                    }}
                  >
                    <X size={14} />
                  </Button>
                  <PMBTooltip target={`remove-file-${file.id}`} text="Hapus" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </FormGroup>
  )
}

export default PMBFileUploads
